import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import AttorneyShell from "@/components/AttorneyShell";
import { PremiumEvidenceWidget } from "@/components/EvidenceIntelligence";
import { Loader2, Sparkles, ShieldCheck, Gauge, ChevronRight, TrendingUp } from "lucide-react";

function Stat({ label, value, accent = "text-navy", sub }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-4" data-testid={`cri-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{label}</div>
      <div className={`mt-1 font-heading text-2xl font-extrabold ${accent}`}>{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function Intelligence() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/attorney/cri-pipeline").then((r) => setData(r.data)).catch(() => setData({ cases: [], summary: {} })); }, []);

  return (
    <AttorneyShell>
      <p className="text-xs font-medium text-slate-500 uppercase tracking-widest flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-cobalt" /> Crash Ready Intelligence™</p>
      <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-navy mt-1">Case Intelligence Pipeline</h1>
      <p className="text-slate-500 mt-1 text-sm">Know which cases are closest to demand, what's blocking the rest, and what to do next.</p>

      {!data ? <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-cobalt animate-spin" /></div> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            <Stat label="Avg Firm CRI" value={data.summary.avg_cri ?? 0} accent="text-cobalt" sub={`${data.summary.total || 0} claimed cases`} />
            <Stat label="Demand Ready" value={data.summary.demand_ready || 0} accent="text-green-600" />
            <Stat label="Almost Ready" value={data.summary.almost_ready || 0} accent="text-amber-600" sub="CRI 81+" />
            <Stat label="Building" value={data.summary.building || 0} accent="text-slate-500" />
          </div>

          <div className="mt-6"><PremiumEvidenceWidget /></div>

          <div className="mt-6 bg-white rounded-3xl border border-slate-100 overflow-hidden">
            <div className="grid grid-cols-[1fr,auto,auto,1fr,auto] gap-3 px-5 py-3 text-xs font-semibold text-slate-400 uppercase border-b border-slate-100">
              <span>Case</span><span>CRI</span><span>Stage</span><span className="hidden md:block">Next Action</span><span></span>
            </div>
            {data.cases.length === 0 ? <p className="p-6 text-slate-400 text-sm">No claimed cases yet.</p> :
              data.cases.map((c) => (
                <Link key={c.case_id} to={`/intelligence/${c.case_id}`} data-testid={`pipeline-row-${c.case_id}`}
                  className="grid grid-cols-[1fr,auto,auto,1fr,auto] gap-3 px-5 py-3.5 items-center border-b border-slate-50 hover:bg-slate-50/60 transition-colors">
                  <div>
                    <div className="font-mono text-sm text-navy">{c.case_number}</div>
                    <div className="text-xs text-slate-400">{c.client_name || "—"}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-heading text-lg font-extrabold" style={{ color: c.grade_color }}>{c.score}</span>
                    <span className="text-xs font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: `${c.grade_color}18`, color: c.grade_color }}>{c.grade}</span>
                  </div>
                  <div>
                    {c.status === "demand_ready" ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-600"><ShieldCheck className="w-3.5 h-3.5" /> Demand Ready™</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-slate-500"><Gauge className="w-3.5 h-3.5" /> {c.stage?.label}</span>
                    )}
                  </div>
                  <div className="hidden md:block text-xs text-slate-500 truncate">{c.top_action || "All tasks complete"}{c.missing_required > 0 && <span className="text-amber-600"> · {c.missing_required} required missing</span>}</div>
                  <ChevronRight className="w-4 h-4 text-slate-300" />
                </Link>
              ))}
          </div>
        </>
      )}
    </AttorneyShell>
  );
}
